import { BrowseArtefactsModalContent } from '@modules/bookmark/components/BrowseArtefactsModalContent';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { DashboardList } from '@modules/dashboard/components/DashboardList';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { SAVED_ARTEFACTS_MODAL_ID } from '@modules/leftNavSidebar/components/SavedNavItem/constants';
import { SpaceDashboardOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import {
  ArtefactEditableByFilter,
  ArtefactType,
  DashboardListItemViewFragment,
  DashboardUseCase,
} from 'generated/graphql';

const CURRENT_USER_DASHBOARDS_TO_SHOW = 5;

interface CurrentUserDashboardListProps {
  dashboards: DashboardListItemViewFragment[] | undefined;
  onClose: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

export const CurrentUserDashboardList = ({
  dashboards,
  onClose,
  hasMore,
  isLoading,
}: CurrentUserDashboardListProps) => {
  const { openModal } = useMuiModalContext();

  const onSeeAllClick = () => {
    trackDashboardEvent({
      eventName: 'See All Your Dashboards Clicked',
    });
    openModal({
      contentComponent: () => (
        <BrowseArtefactsModalContent
          artefactTypes={[
            {
              type: ArtefactType.Dashboard,
              useCase: DashboardUseCase.Default,
            },
          ]}
          editableBy={ArtefactEditableByFilter.User}
          onClickArtefact={onClose}
        />
      ),
      modalProps: {
        id: SAVED_ARTEFACTS_MODAL_ID,
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          sx: { width: '1000px', height: '600px', borderRadius: 2 },
        },
      },
    });
  };

  const currentUserDashboardsToShow = dashboards?.slice(
    0,
    CURRENT_USER_DASHBOARDS_TO_SHOW,
  );

  const renderDashboardList = () => {
    if (isLoading) {
      return (
        <Stack
          py={2}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          <Typography variant="caption" color="text.disabled">
            Loading...
          </Typography>
          <CircularLoader size={16} />
        </Stack>
      );
    }

    if (!currentUserDashboardsToShow?.length) {
      return (
        <Stack p={1} pl={2.5}>
          <Typography variant="caption" color="text.disabled">
            No Dashboards in sight!
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Time to create one and unleash our data-sorcery for insights. Let
            the magic begin!
          </Typography>
        </Stack>
      );
    }

    const hasMoreDashboards =
      (dashboards?.length ?? 0) > CURRENT_USER_DASHBOARDS_TO_SHOW || hasMore;

    return (
      <DashboardList
        dashboards={currentUserDashboardsToShow}
        hasMore={hasMoreDashboards}
        onSeeAllClick={onSeeAllClick}
        onClose={onClose}
        testID="current-user-dashboards-list"
      />
    );
  };

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <SpaceDashboardOutlined sx={{ fontSize: '12px' }} />
        <Typography variant="caption">Your Dashboards</Typography>
      </Stack>
      {renderDashboardList()}
    </Stack>
  );
};
